import { initFunctions, initStorage } from "../tools/conf";
import { demoPhotoForStorageRef } from "../anonymization";
// browser-image-compression (≈235 Ko) n'est charge qu'au premier envoi de
// photo, pas avec le chunk principal (decision du 2026-09-05).
const imageCompression = (...args) =>
  import("browser-image-compression").then((module) => (module.default || module)(...args))


const options = {
  maxSizeMB: 0.25,
};

export class ImageController {
  constructor() {
    // Cache pour les URLs téléchargées
    this.urlCache = new Map();
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 heures en millisecondes
  }

  urlToBase64 = async (url) => {
    try {
      let res = await fetch(url);
      let blob = await res.blob();
      let base64data = await this.blobToData(blob);
      return base64data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  blobToData = (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  resizedataURL = async (datas, wantedWidth, wantedHeight) => {
    return new Promise(async function (resolve, reject) {
      // We create an image to receive the Data URI
      var img = document.createElement("img");

      // When the event "onload" is triggered we can resize the image.
      img.onload = function () {
        // We create a canvas and get its context.
        var canvas = document.createElement("canvas");
        var ctx = canvas.getContext("2d");

        // We set the dimensions at the wanted size.
        canvas.width = wantedWidth;
        canvas.height = wantedHeight;

        // We resize the image with the canvas method drawImage();
        ctx.drawImage(this, 0, 0, wantedWidth, wantedHeight);

        var dataURI = canvas.toDataURL();

        // This is the return of the Promise
        resolve(dataURI);
      };

      // We put the Data URI in the image's src attribute
      img.src = datas;
    });
  };

  getUrlFromStorageRef = async (storageRef, secure = false) => {
    // Mode démo photos : les vues qui résolvent l'image par référence
    // Storage reçoivent une photo factice (jamais la vraie)
    const demoUrl = demoPhotoForStorageRef(storageRef);
    if (demoUrl) return demoUrl;

    try {
      // Vérifier le cache
      const cacheKey = `${storageRef}_${secure}`;
      const cached = this.urlCache.get(cacheKey);
      
      if (cached) {
        const { url, timestamp } = cached;
        const age = Date.now() - timestamp;
        
        // Si le cache est encore valide, retourner l'URL cachée
        if (age < this.cacheExpiry) {
          console.log("[ImageController] Using cached URL for:", storageRef);
          return url;
        } else {
          // Supprimer l'entrée expirée
          this.urlCache.delete(cacheKey);
        }
      }
      
      let url;
      const storageInstance = initStorage();

      // Utiliser refFromURL seulement si storageRef est une vraie URL
      const isUrl =
        storageRef.startsWith("gs://") || storageRef.startsWith("https://");
      const ref = isUrl
        ? storageInstance.refFromURL(storageRef)
        : storageInstance.ref(storageRef);

      if (secure) {
        // MODE SECURE - URL signée via Cloud Function
        const metaData = await ref.getMetadata();
        const cloud = await initFunctions();
        const getSignedUrl = cloud.httpsCallable("getSignedUrl");
        const res = await getSignedUrl({
          storageRef: metaData.fullPath,
        });
        url = res.data.url;
      } else {
        url = await ref.getDownloadURL();
      }

      // Mettre en cache l'URL
      this.urlCache.set(cacheKey, {
        url,
        timestamp: Date.now()
      });
      console.log("[ImageController] Cached new URL for:", storageRef);

      return url;
    } catch (error) {
      console.log("Erreur dans getUrlFromStorageRef:", error);
      return null;
    }
  };

  _handleQuestionIllustrationToBase64 = async (file) => {
    try {
      let output = await imageCompression(file, options);
      let base64data = await this.blobToData(output);
      return {
        success: true,
        base64picture: base64data,
        image: file,
      };
    } catch (error) {
      return {
        success: false,
      };
    }
  };

  async _handleImageUpload(file, folder, preset) {
    let cloud = await initFunctions();

    let base64data = await this.blobToData(file);

    const uploadImage = await cloud.httpsCallable("uploadImage");
    let res = await uploadImage({
      data: {
        name: file.name,
        folder: folder,
        base64picture: base64data,
        preset: preset,
      },
    });

    if (res.data.success) {
      return {
        success: true,
        data: res.data.data,
      };
    } else {
      return {
        success: false,
      };
    }
  }

  async _handleProfilePictureUpdate(type, id, zooId, base64picture) {
    console.log("[ImageController] _handleProfilePictureUpdate called with:", {
      type,
      id,
      zooId,
      base64Length: base64picture?.length,
    });

    let cloud = await initFunctions();
    const uploadProfilePicture = await cloud.httpsCallable(
      "uploadProfilePicture"
    );

    console.log("[ImageController] Calling uploadProfilePicture cloud function...");

    let res;
    try {
      res = await uploadProfilePicture({
        data: {
          type: type,
          id: id,
          zooId: zooId,
          base64picture: base64picture,
        },
      });
      console.log("[ImageController] Cloud function raw response:", res);
      console.log("[ImageController] Cloud function res.data:", res.data);
    } catch (cloudError) {
      console.error("[ImageController] Cloud function threw error:", cloudError);
      console.error("[ImageController] Error code:", cloudError.code);
      console.error("[ImageController] Error message:", cloudError.message);
      console.error("[ImageController] Error details:", cloudError.details);
      return {
        success: false,
        error: cloudError.message,
        code: cloudError.code,
      };
    }

    if (res.data.success) {
      console.log("[ImageController] Upload successful, data:", res.data.data);
      return {
        success: true,
        data: res.data.data,
      };
    } else {
      console.error("[ImageController] Upload failed, full response:", res.data);
      console.error("[ImageController] Error from server:", res.data.error || res.data.message || "No error message");
      return {
        success: false,
        error: res.data.error || res.data.message,
        serverResponse: res.data,
      };
    }
  }

  async _removePicture(public_id) {
    let cloud = await initFunctions();
    const removePicture = await cloud.httpsCallable("removePicture");
    let res = await removePicture({
      public_id: public_id,
    });
    if (res.data.success) {
      return {
        success: true,
        data: res.data.data,
      };
    } else {
      return {
        success: false,
      };
    }
  }

  /**
   * Upload an image to Firebase Storage and return both the storage references and public URLs
   * @param {File} file - The file to upload
   * @param {string} path - The storage path
   * @param {string} fileName - The file name
   * @param {Function} progressCallback - Optional callback for upload progress
   * @returns {Promise<Object>} - Object containing bddRef, thumbBddRef, url, and thumb
   */
  async uploadImage(file, path, fileName, progressCallback = null) {
    try {
      let storage = initStorage();

      // Upload main image
      const mainRef = storage.ref(`${path}/${fileName}`);
      const uploadTask = mainRef.put(file);

      // Handle progress if callback provided
      if (progressCallback) {
        uploadTask.on("state_changed", (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          progressCallback(progress);
        });
      }

      // Wait for upload to complete
      await uploadTask;

      // Get download URL for main image
      const mainUrl = await mainRef.getDownloadURL();
      const mainBddRef = await mainRef.toString();

      // Create and upload thumbnail
      // For simplicity, we're using the same file for thumb in this example
      // In a real implementation, you might want to resize the image first
      const thumbRef = storage.ref(`${path}/thumbs/thumb_${fileName}`);
      await thumbRef.put(file);

      // Get download URL for thumbnail
      const thumbUrl = await thumbRef.getDownloadURL();
      const thumbBddRef = await thumbRef.toString();

      return {
        success: true,
        data: {
          bddRef: mainBddRef,
          url: mainUrl,
          thumbBddRef: thumbBddRef,
          thumb: thumbUrl,
        },
      };
    } catch (error) {
      console.error("Error uploading image:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }
}

// Create a singleton instance to share the cache across all components
let imageControllerInstance = null;

export function getImageController() {
  if (!imageControllerInstance) {
    imageControllerInstance = new ImageController();
  }
  return imageControllerInstance;
}
